import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { Plus, X, Trash2, TrendingUp } from "lucide-react";
import { cmdApi } from "../api";
import { usePermissions } from "../hooks/usePermissions";
import { formatINR, fmtDate } from "../format";
import { fmtPct } from "../fincalc";

const INSTRUMENTS = ["equity", "CCPS", "convertible_note", "SAFE", "debt"];
const DIST_TYPES = ["dividend", "buyback", "interest", "exit", "repayment"];
const INSTRUMENT_LABEL = {
  equity: "Equity", CCPS: "CCPS", convertible_note: "Conv. note", SAFE: "SAFE", debt: "Debt",
};

function Sheet({ onClose, title, children, testId, wide }) {
  return (
    <>
      <div className="cmd-sheet-backdrop" onClick={onClose} />
      <div className="cmd-sheet" style={wide ? { width: "min(720px, 94vw)" } : undefined}
        role="dialog" aria-modal="true" data-testid={testId}>
        <div className="cmd-sheet-head">
          <h2>{title}</h2>
          <button className="cmd-icon-btn" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>
        <div className="cmd-sheet-body">{children}</div>
      </div>
    </>
  );
}

function Kpi({ label, value, sub, testId }) {
  return (
    <div className="cmd-kpi" data-testid={testId}>
      <div className="cmd-kpi-value" style={{ fontSize: 22 }}>{value}</div>
      <div className="cmd-kpi-label">{label}</div>
      {sub && <div className="cmd-kpi-sub">{sub}</div>}
    </div>
  );
}

function fmtMoic(x) { return x === null || x === undefined ? "—" : `${x.toFixed(2)}x`; }
function fmtYears(x) { return x === null || x === undefined ? "—" : `${x.toFixed(1)} yrs`; }

export default function CmdCapital() {
  const { isFounder } = usePermissions();
  const [data, setData] = useState(null);
  const [shareholders, setShareholders] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [distributions, setDistributions] = useState([]);
  const [valuations, setValuations] = useState([]);
  const [sheet, setSheet] = useState(null); // 'investment' | 'distribution' | 'valuation' | 'ledger'
  const [del, setDel] = useState(null);     // { kind, id, label }

  const loadOverview = async () => {
    try { setData((await cmdApi.get("/capital/overview")).data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load capital."); }
  };
  const loadMeta = async () => {
    if (!isFounder) return;
    try {
      const [sh, inv, dist, val] = await Promise.all([
        cmdApi.get("/shareholders"),
        cmdApi.get("/capital/investments"),
        cmdApi.get("/capital/distributions"),
        cmdApi.get("/capital/valuations"),
      ]);
      setShareholders(sh.data.items || []);
      setInvestments(inv.data.items || []);
      setDistributions(dist.data.items || []);
      setValuations(val.data.items || []);
    } catch (e) { /* non-fatal */ }
  };
  useEffect(() => { loadOverview(); loadMeta(); }, []); // eslint-disable-line

  const reload = () => { loadOverview(); loadMeta(); };

  const g = data?.group;
  const rows = data?.rows || [];
  const latestVal = data?.valuation;

  return (
    <section data-testid="cmd-capital">
      <div className="cmd-toolbar" style={{ alignItems: "center" }}>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Capital · Payback & ROI</h1>
        {latestVal && (
          <span className="cmd-chip" data-testid="cap-latest-val">
            Latest valuation: {formatINR(latestVal.equity_value)} · {fmtDate(latestVal.as_of)}{latestVal.basis ? ` · ${latestVal.basis}` : ""}
          </span>
        )}
        {isFounder && (
          <div style={{ marginLeft: "auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className="cmd-btn" onClick={() => setSheet("investment")} data-testid="cap-add-investment">
              <Plus size={14} style={{ marginRight: 5 }} />Add investment
            </button>
            <button className="cmd-btn" onClick={() => setSheet("distribution")} data-testid="cap-add-distribution">
              <Plus size={14} style={{ marginRight: 5 }} />Add distribution
            </button>
            <button className="cmd-btn cmd-btn-ghost" onClick={() => setSheet("valuation")} data-testid="cap-add-valuation">
              Set valuation
            </button>
            <button className="cmd-btn cmd-btn-ghost" onClick={() => setSheet("ledger")} data-testid="cap-ledger">
              Ledger…
            </button>
          </div>
        )}
      </div>

      {!data ? <p className="cmd-muted">Loading…</p> : (
        <>
          <div className="cmd-kpis4" style={{ marginBottom: 14 }}>
            <Kpi label="Total invested" value={formatINR(g.total_invested)} testId="cap-k-invested"
              sub={`${rows.length} shareholder${rows.length === 1 ? "" : "s"} · outstanding ${formatINR(g.outstanding_capital)}`} />
            <Kpi label="Total distributed" value={formatINR(g.total_distributed)} testId="cap-k-distributed"
              sub={g.realised_moic !== null ? `Realised MOIC ${fmtMoic(g.realised_moic)}` : "No distributions yet"} />
            <Kpi label="MOIC (realised + unrealised)" value={fmtMoic(g.moic)} testId="cap-k-moic"
              sub={`Payback ${fmtYears(g.payback_years)}`} />
            <Kpi label="IRR (group)" value={g.irr !== null ? fmtPct(g.irr) : "—"} testId="cap-k-irr"
              sub={g.annualised_return !== null ? `Annualised ${fmtPct(g.annualised_return)}` : "Needs a distribution or valuation"} />
          </div>

          {data.timeline.length > 0 && (
            <div className="cmd-chart-wrap" data-testid="cap-timeline">
              <h3>Cumulative — invested vs distributed</h3>
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={data.timeline} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
                  <defs>
                    <linearGradient id="capInv" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--cmd-accent)" stopOpacity={0.5} />
                      <stop offset="95%" stopColor="var(--cmd-accent)" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="capDist" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--cmd-health-ok)" stopOpacity={0.5} />
                      <stop offset="95%" stopColor="var(--cmd-health-ok)" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--cmd-line)" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "var(--cmd-muted)" }}
                    axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "var(--cmd-muted)" }} axisLine={false} tickLine={false}
                    tickFormatter={(v) => formatINR(v)} width={70} />
                  <Tooltip formatter={(v, name) => [formatINR(v),
                    name === "cum_invested" ? "Cumulative invested" :
                    name === "cum_distributed" ? "Cumulative distributed" : "Outstanding"]}
                    contentStyle={{ background: "var(--cmd-raised)", border: "1px solid var(--cmd-line)", borderRadius: 10, fontSize: 13 }} />
                  <Area type="monotone" dataKey="cum_invested" stroke="var(--cmd-accent)" fill="url(#capInv)" strokeWidth={2} />
                  <Area type="monotone" dataKey="cum_distributed" stroke="var(--cmd-health-ok)" fill="url(#capDist)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="cmd-pl-wrap" data-testid="cap-table" style={{ marginTop: 14 }}>
            {rows.length === 0 ? (
              <div className="cmd-empty"><h3>No capital activity yet</h3>
                <p>{isFounder ? "Add the first investment to kick things off." : "Nothing recorded against your account yet."}</p></div>
            ) : (
              <table className="cmd-pl">
                <thead>
                  <tr>
                    <th>Shareholder</th><th>Instrument</th><th>Holding %</th>
                    <th>Invested</th><th>Distributed</th><th>Outstanding</th>
                    <th>MOIC</th><th>IRR</th><th>Payback</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.shareholder_id} data-testid={`cap-row-${r.shareholder_id}`}>
                      <td>{r.name}</td>
                      <td><span className="cmd-chip" style={{ fontSize: 11 }}>{INSTRUMENT_LABEL[r.instrument] || r.instrument || "—"}</span></td>
                      <td>{r.holding_pct !== null && r.holding_pct !== undefined ? `${r.holding_pct}%` : "—"}</td>
                      <td>{formatINR(r.total_invested)}</td>
                      <td>{formatINR(r.total_distributed)}</td>
                      <td>{formatINR(r.outstanding_capital)}</td>
                      <td>{fmtMoic(r.moic)}</td>
                      <td>{r.irr !== null ? fmtPct(r.irr) : "—"}</td>
                      <td>{fmtYears(r.payback_years)}</td>
                    </tr>
                  ))}
                  <tr className="cmd-pl-total">
                    <td>Group total</td><td></td><td></td>
                    <td>{formatINR(g.total_invested)}</td>
                    <td>{formatINR(g.total_distributed)}</td>
                    <td>{formatINR(g.outstanding_capital)}</td>
                    <td>{fmtMoic(g.moic)}</td>
                    <td>{g.irr !== null ? fmtPct(g.irr) : "—"}</td>
                    <td>{fmtYears(g.payback_years)}</td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>

          {data.verticals.length > 0 && (
            <div className="cmd-raised" style={{ padding: 16, borderRadius: 14, marginTop: 14 }}
                 data-testid="cap-vertical-contribution">
              <h3 style={{ margin: "0 0 10px", fontSize: 14, fontWeight: 600 }}>
                <TrendingUp size={14} style={{ verticalAlign: "-2px", marginRight: 6 }} />
                Per-vertical ROI contribution · latest FY
              </h3>
              <div className="cmd-table-wrap">
                <table className="cmd-pl" style={{ minWidth: 480 }}>
                  <thead><tr><th>Vertical</th><th>Revenue</th><th>EBITDA</th><th>Share of revenue</th><th>Your attribution*</th></tr></thead>
                  <tbody>
                    {data.verticals.map((v) => {
                      const myRow = rows[0]; // Investor view has one row; founder sees own name here if linked.
                      const attribution = myRow && myRow.holding_pct && v.ebitda !== null
                        ? v.ebitda * myRow.holding_pct / 100 : null;
                      return (
                        <tr key={v.vertical} data-testid={`cap-v-${v.vertical.replace(/\s/g, "-")}`}>
                          <td>{v.vertical}</td>
                          <td>{v.revenue !== null ? formatINR(v.revenue) : "—"}</td>
                          <td className={v.ebitda < 0 ? "neg" : ""}>{v.ebitda !== null ? formatINR(v.ebitda) : "—"}</td>
                          <td>{v.share !== null ? `${(v.share * 100).toFixed(1)}%` : "—"}</td>
                          <td>{attribution !== null ? formatINR(attribution) : "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="cmd-muted" style={{ fontSize: 11, marginTop: 6, marginBottom: 0 }}>
                * Attribution = your holding % × vertical EBITDA. Illustrative only — distributions follow Board policy.
              </p>
            </div>
          )}
        </>
      )}

      {sheet === "investment" && isFounder && (
        <Sheet onClose={() => setSheet(null)} title="Add investment" testId="investment-sheet">
          <AddInvestment shareholders={shareholders} onDone={() => { setSheet(null); reload(); }} />
        </Sheet>
      )}
      {sheet === "distribution" && isFounder && (
        <Sheet onClose={() => setSheet(null)} title="Add distribution" testId="distribution-sheet">
          <AddDistribution shareholders={shareholders} onDone={() => { setSheet(null); reload(); }} />
        </Sheet>
      )}
      {sheet === "valuation" && isFounder && (
        <Sheet onClose={() => setSheet(null)} title="Set company valuation" testId="valuation-sheet">
          <AddValuation onDone={() => { setSheet(null); reload(); }} />
        </Sheet>
      )}
      {sheet === "ledger" && isFounder && (
        <Sheet onClose={() => setSheet(null)} title="Capital ledger" testId="ledger-sheet" wide>
          <Ledger
            investments={investments} distributions={distributions} valuations={valuations}
            shareholders={shareholders}
            onDelete={(kind, id, label) => setDel({ kind, id, label })} />
        </Sheet>
      )}
      {del && isFounder && (
        <Sheet onClose={() => setDel(null)} title="Remove entry — reason required" testId="cap-delete-sheet">
          <DeleteWithReason item={del} onCancel={() => setDel(null)}
            onDone={() => { setDel(null); reload(); }} />
        </Sheet>
      )}
    </section>
  );
}

function AddInvestment({ shareholders, onDone }) {
  const [sh, setSh] = useState(shareholders[0]?.id || "");
  const [instrument, setInstrument] = useState("equity");
  const [amount, setAmount] = useState("");
  const [dt, setDt] = useState(new Date().toISOString().slice(0, 10));
  const [preMoney, setPreMoney] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await cmdApi.post("/capital/investments", {
        shareholder_id: sh, instrument, amount: Number(amount), date: dt,
        pre_money_valuation: preMoney ? Number(preMoney) : null, notes: notes || null,
      });
      toast.success("Investment recorded"); onDone();
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Could not save."); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit}>
      <div className="cmd-form-grid">
        <div className="cmd-field"><label>Shareholder</label>
          <select className="cmd-input" value={sh} onChange={(e) => setSh(e.target.value)} required data-testid="inv-sh">
            <option value="" disabled>Pick a shareholder</option>
            {shareholders.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select></div>
        <div className="cmd-field"><label>Instrument</label>
          <select className="cmd-input" value={instrument} onChange={(e) => setInstrument(e.target.value)} data-testid="inv-inst">
            {INSTRUMENTS.map((i) => <option key={i} value={i}>{INSTRUMENT_LABEL[i]}</option>)}
          </select></div>
        <div className="cmd-field"><label>Amount (₹)</label>
          <input className="cmd-input" inputMode="decimal" required value={amount}
            onChange={(e) => setAmount(e.target.value)} data-testid="inv-amount" /></div>
        <div className="cmd-field"><label>Date</label>
          <input type="date" className="cmd-input" required value={dt}
            onChange={(e) => setDt(e.target.value)} data-testid="inv-date" /></div>
        <div className="cmd-field"><label>Pre-money valuation (optional)</label>
          <input className="cmd-input" inputMode="decimal" value={preMoney}
            onChange={(e) => setPreMoney(e.target.value)} data-testid="inv-pre" /></div>
      </div>
      <div className="cmd-field"><label>Notes</label>
        <input className="cmd-input" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="inv-notes" /></div>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button type="submit" className="cmd-btn cmd-btn-primary" disabled={busy} data-testid="inv-save">
          {busy ? "Saving…" : "Save investment"}
        </button>
      </div>
    </form>
  );
}

function AddDistribution({ shareholders, onDone }) {
  const [sh, setSh] = useState(shareholders[0]?.id || "");
  const [type, setType] = useState("dividend");
  const [amount, setAmount] = useState("");
  const [dt, setDt] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await cmdApi.post("/capital/distributions", {
        shareholder_id: sh, type, amount: Number(amount), date: dt, notes: notes || null,
      });
      toast.success("Distribution recorded"); onDone();
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Could not save."); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit}>
      <div className="cmd-form-grid">
        <div className="cmd-field"><label>Shareholder</label>
          <select className="cmd-input" value={sh} onChange={(e) => setSh(e.target.value)} required data-testid="dist-sh">
            <option value="" disabled>Pick a shareholder</option>
            {shareholders.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select></div>
        <div className="cmd-field"><label>Type</label>
          <select className="cmd-input" value={type} onChange={(e) => setType(e.target.value)} data-testid="dist-type">
            {DIST_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select></div>
        <div className="cmd-field"><label>Amount (₹)</label>
          <input className="cmd-input" inputMode="decimal" required value={amount}
            onChange={(e) => setAmount(e.target.value)} data-testid="dist-amount" /></div>
        <div className="cmd-field"><label>Date</label>
          <input type="date" className="cmd-input" required value={dt}
            onChange={(e) => setDt(e.target.value)} data-testid="dist-date" /></div>
      </div>
      <div className="cmd-field"><label>Notes</label>
        <input className="cmd-input" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="dist-notes" /></div>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button type="submit" className="cmd-btn cmd-btn-primary" disabled={busy} data-testid="dist-save">
          {busy ? "Saving…" : "Save distribution"}
        </button>
      </div>
    </form>
  );
}

function AddValuation({ onDone }) {
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));
  const [equity, setEquity] = useState("");
  const [basis, setBasis] = useState("Management mark");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await cmdApi.post("/capital/valuations", {
        as_of: asOf, equity_value: Number(equity), basis, notes: notes || null,
      });
      toast.success("Valuation recorded"); onDone();
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Could not save."); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit}>
      <div className="cmd-form-grid">
        <div className="cmd-field"><label>As of</label>
          <input type="date" className="cmd-input" required value={asOf}
            onChange={(e) => setAsOf(e.target.value)} data-testid="val-asof" /></div>
        <div className="cmd-field"><label>Equity value (₹)</label>
          <input className="cmd-input" inputMode="decimal" required value={equity}
            onChange={(e) => setEquity(e.target.value)} data-testid="val-equity" /></div>
        <div className="cmd-field"><label>Basis</label>
          <input className="cmd-input" value={basis} onChange={(e) => setBasis(e.target.value)} data-testid="val-basis" /></div>
      </div>
      <div className="cmd-field"><label>Notes</label>
        <input className="cmd-input" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="val-notes" /></div>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button type="submit" className="cmd-btn cmd-btn-primary" disabled={busy} data-testid="val-save">
          {busy ? "Saving…" : "Save valuation"}
        </button>
      </div>
    </form>
  );
}

function Ledger({ investments, distributions, valuations, shareholders, onDelete }) {
  const nameOf = useMemo(() => Object.fromEntries(shareholders.map((s) => [s.id, s.name])), [shareholders]);
  const row = (kind, items, labelCols) => (
    <>
      <tr className="cmd-pl-subtotal"><td colSpan={labelCols + 2} style={{ textAlign: "left" }}>{kind}</td></tr>
      {items.length === 0
        ? <tr><td colSpan={labelCols + 2} className="cmd-muted">None recorded.</td></tr>
        : items.map((it) => (
          <tr key={it.id} data-testid={`ledger-${kind.toLowerCase()}-${it.id}`}>
            <td>{it.date || it.as_of}</td>
            <td>{kind === "Valuations" ? (it.basis || "—") : (nameOf[it.shareholder_id] || "—")}</td>
            <td>{kind === "Investments" ? (INSTRUMENT_LABEL[it.instrument] || it.instrument) :
                 kind === "Distributions" ? it.type : "—"}</td>
            <td>{formatINR(it.amount || it.equity_value)}</td>
            <td style={{ textAlign: "right" }}>
              <button className="cmd-link" style={{ width: "auto", padding: "2px 8px", color: "var(--cmd-health-risk)" }}
                onClick={() => onDelete(kind.toLowerCase().replace(/s$/, ""), it.id,
                  `${kind.slice(0, -1)} on ${it.date || it.as_of} · ${formatINR(it.amount || it.equity_value)}`)}
                data-testid={`ledger-del-${it.id}`}>Remove…</button>
            </td>
          </tr>
        ))}
    </>
  );
  return (
    <div className="cmd-pl-wrap">
      <table className="cmd-pl">
        <thead><tr><th>Date</th><th>Who / basis</th><th>Kind</th><th>Amount</th><th></th></tr></thead>
        <tbody>
          {row("Investments", investments, 2)}
          {row("Distributions", distributions, 2)}
          {row("Valuations", valuations, 2)}
        </tbody>
      </table>
    </div>
  );
}

function DeleteWithReason({ item, onCancel, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const ok = reason.trim().length >= 10;
  const submit = async () => {
    setBusy(true);
    try {
      const path = item.kind === "investment" ? "investments"
        : item.kind === "distribution" ? "distributions" : "valuations";
      await cmdApi.delete(`/capital/${path}/${item.id}`, { data: { reason: reason.trim() } });
      toast.success("Removed · reason recorded in audit log"); onDone();
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Could not remove."); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <div className="cmd-danger-box">
        Removing: <strong>{item.label}</strong>. This is permanent and will be captured in the audit log.
      </div>
      <div className="cmd-field" style={{ marginTop: 14 }}>
        <label>Reason (at least 10 characters)</label>
        <textarea className="cmd-input" rows={3} value={reason}
          onChange={(e) => setReason(e.target.value)} data-testid="cap-del-reason"
          placeholder="e.g. Duplicate entry, wrong shareholder, superseded by new round…" />
      </div>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 12 }}>
        <button className="cmd-btn cmd-btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
        <button className="cmd-btn cmd-btn-primary" disabled={!ok || busy}
          style={{ background: "var(--cmd-health-risk)" }}
          onClick={submit} data-testid="cap-del-confirm">
          {busy ? "Removing…" : "Confirm — remove"}
        </button>
      </div>
    </div>
  );
}
